import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/Card";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../ui/Table";
import { Badge } from "../ui/Badge";
import { formatNumber, formatDecimal, formatPercent, formatMonth } from "../FinancialReview/format";
import { KpiCard, TablePager, usePagedFetch } from "../FinancialReview/ui";
import { fetchLtvProviders } from "./api";

const UNMAPPED = "Unmapped";
const UNKNOWN_SPECIALTY = "Unknown";

const USAGE_MONTH_BUCKETS = [
  { key: "count_1", label: "1 month", color: "#EEE6F8" },
  { key: "count_2_3", label: "2-3 months", color: "#C9B6E8" },
  { key: "count_4_6", label: "4-6 months", color: "#9B6FCF" },
  { key: "count_7_12", label: "7-12 months", color: "#5C3AA6" },
  { key: "count_13_24", label: "13-24 months", color: "#1A1440" },
  { key: "count_25_plus", label: "25+ months", color: "#5EC4C8" },
];

function orgLabel(row) {
  return row.super_org || UNMAPPED;
}

function UsageMonthsOrgChart({ row }) {
  const [hover, setHover] = useState(null);
  const name = orgLabel(row);
  const total = row.doctor_count || 0;
  const hoverText = hover
    ? `${hover.label}: ${formatNumber(hover.count)} ${hover.count === 1 ? "user" : "users"} (${hover.pct.toFixed(0)}%)`
    : "Hover a segment to see the count";
  return (
    <div className="rounded-lg bg-[#F3EEF9] p-4">
      <p className="text-sm font-semibold text-[#5C3AA6] mb-4">
        {name} user usage months | avg tenure {row.tenure_months} mo
      </p>
      <div className="flex gap-3">
        <span
          className="text-[11px] text-muted-foreground self-center shrink-0"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Customer
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs font-medium truncate" title={name}>
              {name}
            </span>
            <div className="flex-1 flex h-10 gap-0.5 min-w-0 overflow-hidden rounded">
              {USAGE_MONTH_BUCKETS.map((bucket) => {
                const count = row[bucket.key] || 0;
                if (!total || !count) return null;
                const pct = (count / total) * 100;
                const active = hover?.key === bucket.key;
                return (
                  <div
                    key={bucket.key}
                    className="h-full cursor-pointer"
                    style={{ width: `${pct}%`, background: bucket.color, opacity: hover && !active ? 0.45 : 1 }}
                    onMouseEnter={() => setHover({ key: bucket.key, label: bucket.label, count, pct })}
                    onMouseLeave={() => setHover(null)}
                  />
                );
              })}
            </div>
          </div>
          <div className="flex justify-between text-[11px] text-muted-foreground mt-1" style={{ marginLeft: "6.5rem" }}>
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
          </div>
          <p className="text-center text-xs font-medium mt-2 min-h-[1.25rem]">{hoverText}</p>
          <p className="text-center text-[11px] text-muted-foreground">Share of users</p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-3 text-[11px] text-muted-foreground">
            {USAGE_MONTH_BUCKETS.map((bucket) => {
              const count = row[bucket.key] || 0;
              const pct = total ? (count / total) * 100 : 0;
              return (
                <span
                  key={bucket.key}
                  className="inline-flex items-center gap-1.5 cursor-pointer"
                  style={{ opacity: hover && hover.key !== bucket.key ? 0.45 : 1 }}
                  onMouseEnter={() => setHover({ key: bucket.key, label: bucket.label, count, pct })}
                  onMouseLeave={() => setHover(null)}
                >
                  <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: bucket.color }} />
                  {bucket.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export function averagedUsageRow(rows) {
  const out = { super_org: "All super orgs", doctor_count: 0, tenure_months: 0 };
  for (const bucket of USAGE_MONTH_BUCKETS) out[bucket.key] = 0;
  if (!rows.length) return out;
  let tenureSum = 0;
  for (const row of rows) {
    const n = row.doctor_count || 0;
    out.doctor_count += n;
    tenureSum += (row.tenure_months || 0) * n;
    for (const bucket of USAGE_MONTH_BUCKETS) out[bucket.key] += row[bucket.key] || 0;
  }
  out.tenure_months = out.doctor_count ? Math.round(tenureSum / out.doctor_count) : 0;
  return out;
}

function UsageMonthsChart({ rows, superOrg }) {
  if (!rows.length) {
    return <p className="text-sm text-muted-foreground">No users with recorded activity match the selected filters.</p>;
  }
  const selected = Array.isArray(superOrg) ? superOrg : superOrg ? [superOrg] : [];
  if (selected.length > 1) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {rows.map((row) => (
          <UsageMonthsOrgChart key={orgLabel(row)} row={row} />
        ))}
      </div>
    );
  }
  if (selected.length === 1) return <UsageMonthsOrgChart row={rows[0]} />;
  return <UsageMonthsOrgChart row={averagedUsageRow(rows)} />;
}

function collapseLoyalty(heatmap, key, fallback) {
  const by = new Map();
  for (const cell of heatmap) {
    const label = cell[key] || fallback;
    const users = cell.provider_count || 0;
    const current = by.get(label) || { [key]: label, provider_count: 0, streakSum: 0 };
    current.provider_count += users;
    current.streakSum += (cell.avg_longest_streak_months || 0) * users;
    by.set(label, current);
  }
  return [...by.values()]
    .map((row) => ({
      [key]: row[key],
      provider_count: row.provider_count,
      avg_longest_streak_months: row.provider_count ? row.streakSum / row.provider_count : 0,
    }))
    .sort(
      (a, b) =>
        b.avg_longest_streak_months - a.avg_longest_streak_months ||
        b.provider_count - a.provider_count ||
        String(a[key]).localeCompare(String(b[key]))
    );
}

function LoyaltyRank({ rows, labelKey, emptyLabel }) {
  const maxStreak = Math.max(...rows.map((row) => row.avg_longest_streak_months || 0), 1);
  if (rows.length === 0) return <p className="text-sm text-muted-foreground">{emptyLabel}</p>;
  return (
    <div className="space-y-2 max-h-96 overflow-y-auto">
      {rows.map((row) => {
        const streak = row.avg_longest_streak_months || 0;
        const widthPct = Math.max((streak / maxStreak) * 100, 4);
        return (
          <div key={row[labelKey]} className="flex items-center gap-2">
            <span className="w-40 shrink-0 truncate text-xs font-medium" title={row[labelKey]}>
              {row[labelKey]}
            </span>
            <div className="flex-1 h-7 rounded bg-muted overflow-hidden">
              <div className="h-full bg-purple-500" style={{ width: `${widthPct}%` }} />
            </div>
            <span className="w-20 shrink-0 text-right text-xs font-medium">{formatDecimal(streak, 1)} months</span>
            <span className="w-20 shrink-0 text-right text-[10px] text-muted-foreground">
              {formatNumber(row.provider_count)} {row.provider_count === 1 ? "user" : "users"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function LtvTab({ data, loading, error, superOrg, specialty }) {
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setQApplied(q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);
  const pager = usePagedFetch(
    fetchLtvProviders,
    { super_org: superOrg, specialty, q: qApplied },
    { enabled: true }
  );

  if (loading && !data) return <p className="text-sm text-muted-foreground">Loading Customer LTV report…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!data) return null;

  const { summary, heatmap = [], usage_months = [] } = data;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Customer LTV</h2>
        <p className="text-sm text-muted-foreground">
          Usage tenure — not a dollar figure, and not scoped to report month. Months
          active is distinct calendar months with recordings. Longest streak is consecutive
          active months. Currently active means a recording in the last 30 days.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label={summary.total_count === 1 ? "User (filtered)" : "Users (filtered)"} value={formatNumber(summary.total_count)} />
        <KpiCard label="Avg months active" value={formatDecimal(summary.avg_months_active, 1)} />
        <KpiCard label="Avg longest streak" value={`${formatDecimal(summary.avg_longest_streak_months, 1)} months`} />
        <KpiCard label="Currently active" value={formatPercent(summary.pct_currently_active)} />
      </div>

      <UsageMonthsChart rows={usage_months} superOrg={superOrg} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Loyalty by super org</CardTitle>
            <CardDescription>Bar length = average longest consecutive-month streak.</CardDescription>
          </CardHeader>
          <CardContent>
            <LoyaltyRank
              rows={collapseLoyalty(heatmap, "super_org", UNMAPPED)}
              labelKey="super_org"
              emptyLabel="No users with recorded activity match the selected filters."
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Loyalty by specialty</CardTitle>
            <CardDescription>Same streak signal, rolled up across orgs.</CardDescription>
          </CardHeader>
          <CardContent>
            <LoyaltyRank
              rows={collapseLoyalty(heatmap, "specialty", UNKNOWN_SPECIALTY)}
              labelKey="specialty"
              emptyLabel="No users with recorded activity match the selected filters."
            />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Returning users</CardTitle>
          <CardDescription>
            Ranked by months active, then longest consecutive streak
            {pager.total ? ` · ${formatNumber(pager.total)} match` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <input
            type="search"
            className="flex h-10 w-full max-w-sm rounded-md border border-input bg-background px-3 py-2 text-sm mb-3"
            placeholder="Search name, super org, or specialty"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Super org</TableHead>
                  <TableHead>Specialty</TableHead>
                  <TableHead>First active</TableHead>
                  <TableHead>Last active</TableHead>
                  <TableHead>Months active</TableHead>
                  <TableHead>Longest streak</TableHead>
                  <TableHead>Total recordings</TableHead>
                  <TableHead>Currently active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pager.rows.map((row) => (
                  <TableRow key={row.user_id}>
                    <TableCell>{row.user_name || "—"}</TableCell>
                    <TableCell>{row.user_email || "—"}</TableCell>
                    <TableCell>{row.super_org || "—"}</TableCell>
                    <TableCell>{row.specialty || "—"}</TableCell>
                    <TableCell>{formatMonth(row.first_active_month)}</TableCell>
                    <TableCell>{formatMonth(row.last_active_month)}</TableCell>
                    <TableCell>{formatNumber(row.months_active)}</TableCell>
                    <TableCell>{formatNumber(row.longest_streak_months)} months</TableCell>
                    <TableCell>{formatNumber(row.total_recordings)}</TableCell>
                    <TableCell>
                      {row.currently_active ? (
                        <Badge variant="outline" className="text-green-600">
                          Active
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-muted-foreground">
                          Lapsed
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!pager.loading && pager.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground">
                      No users with recorded activity match the selected filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {pager.loading && <p className="text-xs text-muted-foreground text-center py-3">Loading…</p>}
          {pager.error && <p className="text-xs text-red-600 text-center py-3">{pager.error}</p>}
          <TablePager page={pager.page} totalPages={pager.totalPages} onPageChange={pager.setPage} />
        </CardContent>
      </Card>
    </div>
  );
}
